import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Leaf,
  LogOut,
  Filter,
  Clock,
  Check,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ContactRequestModal } from '../components/ContactRequestModal';

interface ConsultationRequest {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  location: string;
  service: string | null;
  preferred_days: string[];
  preferred_times: string[];
  previous_patient: boolean;
  concerns: string;
  contact_preference: 'email' | 'phone' | 'either';
  status: 'pending' | 'in_progress' | 'completed' | 'archived';
}

interface ContactRequest {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  contact_preference: 'email' | 'phone' | 'either';
  message: string;
  status: 'pending' | 'in_progress' | 'completed' | 'archived';
  internal_notes: string | null;
  updated_at: string;
  responded_at: string | null;
  responded_by: string | null;
}

export function WebsiteMessages() {
  const navigate = useNavigate();
  const { user, userRoles, signOut } = useAuth();
  const [consultationRequests, setConsultationRequests] = useState<ConsultationRequest[]>([]);
  const [contactRequests, setContactRequests] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [consultationFilters, setConsultationFilters] = useState({
    status: 'not_completed', // Changed default to exclude completed
    location: 'all',
    contact_preference: 'all'
  });
  const [contactFilters, setContactFilters] = useState({
    status: 'not_completed', // Changed default to exclude completed
    contact_preference: 'all'
  });
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [selectedContactRequest, setSelectedContactRequest] = useState<ContactRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
    // Auto-dismiss alerts after 5 seconds
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const includeCompletedConsultation = consultationFilters.status === 'all' || consultationFilters.status === 'completed';
      const includeCompletedContact = contactFilters.status === 'all' || contactFilters.status === 'completed';

      // Fetch consultation requests
      const { data: consultationData, error: consultationError } = await supabase.rpc('get_consultation_requests', {
        include_completed: includeCompletedConsultation
      });
      if (consultationError) throw consultationError;
      setConsultationRequests(consultationData || []);

      // Fetch contact requests
      const { data: contactData, error: contactError } = await supabase.rpc('get_contact_requests', {
        include_completed: includeCompletedContact
      });
      if (contactError) throw contactError;
      setContactRequests(contactData || []);
    } catch (err: any) {
      console.error('Error fetching requests:', err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    type: 'consultation' | 'contact',
    id: string,
    newStatus: 'pending' | 'in_progress' | 'completed' | 'archived'
  ) => {
    try {
      if (type === 'consultation') {
        const { error } = await supabase.rpc('update_consultation_request', {
          request_id: id,
          new_status: newStatus
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc('update_contact_request', {
          request_id: id,
          new_status: newStatus
        });
        if (error) throw error;
      }
      setSuccess('Status updated successfully');
      fetchRequests();
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleFilterChange = (
    type: 'consultation' | 'contact',
    filterType: string,
    value: string
  ) => {
    if (type === 'consultation') {
      setConsultationFilters(prev => ({ ...prev, [filterType]: value }));
    } else {
      setContactFilters(prev => ({ ...prev, [filterType]: value }));
    }
    fetchRequests(); // Refresh data when filters change
  };

  const handleViewContactRequest = (request: ContactRequest) => {
    setSelectedContactRequest(request);
    setIsModalOpen(true);
  };

  const filteredConsultationRequests = consultationRequests.filter(request => {
    return (
      (consultationFilters.status === 'all' || 
       (consultationFilters.status === 'not_completed' && request.status !== 'completed') ||
       request.status === consultationFilters.status) &&
      (consultationFilters.location === 'all' || request.location === consultationFilters.location) &&
      (consultationFilters.contact_preference === 'all' || request.contact_preference === consultationFilters.contact_preference)
    );
  }).sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortBy === 'created_at_desc' ? dateB - dateA : dateA - dateB;
  });

  const filteredContactRequests = contactRequests.filter(request => {
    return (
      (contactFilters.status === 'all' || 
       (contactFilters.status === 'not_completed' && request.status !== 'completed') ||
       request.status === contactFilters.status) &&
      (contactFilters.contact_preference === 'all' || request.contact_preference === contactFilters.contact_preference)
    );
  }).sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortBy === 'created_at_desc' ? dateB - dateA : dateA - dateB;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">Website Messages</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">{user?.email}</span>
              <div className="flex flex-wrap gap-2">
                {userRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {role}
                  </span>
                ))}
              </div>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          {/* Hero Banner */}
          <div className="bg-white shadow-lg rounded-lg mb-8 overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              <div className="relative px-8 py-12">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-white hover:text-green-100 transition-colors duration-200"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                  <Leaf className="h-12 w-12 text-white opacity-20" />
                </div>
                <div className="mt-4 text-center">
                  <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                  <p className="text-green-100 text-lg mb-4">
                    Tracking Health and Reducing Illness Via Efficiency
                  </p>
                  <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                  <h2 className="text-2xl font-semibold text-white mt-4">Website Messages</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-6 px-6 py-4 bg-red-50 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          {success && (
            <div className="mb-6 px-6 py-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-400" />
                <p className="ml-3 text-sm text-green-700">{success}</p>
              </div>
            </div>
          )}

          {/* Consultation Requests Section */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Consultation Requests</h3>
              </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={consultationFilters.status}
                    onChange={(e) => handleFilterChange('consultation', 'status', e.target.value)}
                  >
                    <option value="not_completed">Not Completed</option>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={consultationFilters.location}
                    onChange={(e) => handleFilterChange('consultation', 'location', e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="chico">Chico</option>
                    <option value="redding">Redding</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Preference</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={consultationFilters.contact_preference}
                    onChange={(e) => handleFilterChange('consultation', 'contact_preference', e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="either">Either</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sort By</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="created_at_desc">Newest First</option>
                    <option value="created_at_asc">Oldest First</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">Loading...</td>
                    </tr>
                  ) : filteredConsultationRequests.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">No consultation requests found</td>
                    </tr>
                  ) : (
                    filteredConsultationRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{`${request.first_name} ${request.last_name}`}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.location}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                            value={request.status}
                            onChange={(e) => handleStatusUpdate('consultation', request.id, e.target.value as any)}
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="archived">Archived</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900">View</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Contact Requests Section */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Contact Messages</h3>
              </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={contactFilters.status}
                    onChange={(e) => handleFilterChange('contact', 'status', e.target.value)}
                  >
                    <option value="not_completed">Not Completed</option>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Preference</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={contactFilters.contact_preference}
                    onChange={(e) => handleFilterChange('contact', 'contact_preference', e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="either">Either</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sort By</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="created_at_desc">Newest First</option>
                    <option value="created_at_asc">Oldest First</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">Loading...</td>
                    </tr>
                  ) : filteredContactRequests.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">No contact messages found</td>
                    </tr>
                  ) : (
                    filteredContactRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{`${request.first_name} ${request.last_name}`}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.email || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.phone || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                            value={request.status}
                            onChange={(e) => handleStatusUpdate('contact', request.id, e.target.value as any)}
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="archived">Archived</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            onClick={() => handleViewContactRequest(request)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Contact Request Modal */}
      <ContactRequestModal
        request={selectedContactRequest}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdate={fetchRequests}
      />
    </div>
  );
}